import { FC, useState, useMemo } from "react";

import { capitalize, isEmpty } from "lodash";

import { useRouter } from "next/router";

import {
  Button,
  Card,
  Input,
  Form,
  Empty,
  Upload,
  Switch,
  Tooltip,
  Dropdown,
} from "antd";

import imageCompression from "browser-image-compression";

import ProfileCardPosted from "@/components/pages/profile/ProfileCardPosted";
import CardUserProfile from "@/components/Card/card-user-profile";

import {
  HiChevronDoubleRight,
  HiOutlineDocumentText,
  HiTrash,
  HiPhotograph,
} from "react-icons/hi";

import useAuthStore from "@/store/useAuthStore";

import { useCreatePost, useGetPostsByAuthorId } from "@/hooks/usePost";
import { uploadFile } from "@/hooks/useUploadFile";

import { DEFAULT_COMPRESS_OPTIONS } from "@/utils/constants";

import { IPayloadCreatePost } from "@/types";
import { truncateText, userTypeConvert } from "@/utils/helper";
import {
  FileImageFilled,
  FileImageOutlined,
  QuestionCircleFilled,
  SendOutlined,
} from "@ant-design/icons";
import { FaRegImage } from "react-icons/fa";
import { BsChevronDown } from "react-icons/bs";
import ProfileModalBroadcast, { postBroadcast } from "./ProfileModalBroadcast";
import useMyProfileStore from "@/hooks/useMyProfileStore";
import useWindowResize from "@/hooks/useWindowResize";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useIsViewAsHR } from "@/hooks/useIsViewAsHR";

interface IProfileSectionPosts {
  isPost: boolean;
  dataUser: any;
  onSuccess?: any;
  onError?: any;
}

const ProfileSectionPosts: FC<IProfileSectionPosts> = ({
  isPost,
  dataUser,
  onSuccess,
  onError,
}) => {
  const [form] = Form.useForm();
  const isMobile = useWindowResize();

  const authUser = useAuthStore.useUser();
  const { userView } = useMyProfileStore();

  const { isViewAsHR } = useIsViewAsHR();

  const [isProcess, setIsProcess] = useState<boolean>(false);
  const [fileRaw, setFileRaw] = useState<any>();
  const [isOpenBroadcastModal, isOpenBroadcastModalSet] =
    useState<boolean>(false);
  const [isPostBroadcastedTo, isPostBroadcastedToSet] = useState<postBroadcast>(
    { audienceType: "PRIVATE" },
  );
  const { track } = useAnalytics();

  const {
    data,
    isFetching: isLoading,
    isError,
    refetch,
  } = useGetPostsByAuthorId(dataUser?.user?.id, "USER", "POST");

  const handleCreatePost = async (values: any) => {
    setIsProcess(true);

    try {
      const payload: IPayloadCreatePost = {
        contextType: "USER",
        category: "POST",
        contextId: dataUser?.user?.id,
        content: values?.content,
        attachments: [],
        postAuthorId: authUser?.profile?.user_id,
        organizationGroup: authUser?.profile?.["cognito:groups"]?.[0],
        audienceType: isPostBroadcastedTo.audienceType,
        audienceID: isPostBroadcastedTo.audienceID,
      };

      if (isViewAsHR) {
        payload.isAuthorHR = true;
      }
      let fileType = 'no'
      if (fileRaw) {
        const dataFile: File = fileRaw?.originFileObj;
        const keyImg = `user/post/${dataUser?.user?.id}/${Date.now()}-${fileRaw?.name}`;
      
        // Check if the file is an image
        if (dataFile.type.startsWith("image/")) {
          const compressedFile = await imageCompression(dataFile, DEFAULT_COMPRESS_OPTIONS);
          await uploadFile({ key: keyImg, file: compressedFile });
        } else {
          // If not an image, upload directly
          await uploadFile({ key: keyImg, file: dataFile });
        }

        fileType =  dataFile.type
      
        payload.attachments = [keyImg];
      }

      await useCreatePost(payload);
      track("Create Post", "Submit", `The post has ${fileType} file`); 
      form.resetFields();
      setFileRaw(undefined);
      refetch();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      track("Create Post", "Submit", "Failed")
      if (onError) {
        onError();
      }

      console.log(error);
    } finally {
      setIsProcess(false);
    }
  };

  const myDepartment = useMemo(() => {
    return authUser?.bio?.user?.department || userView?.user?.department;
  }, [authUser, userView]);

  return (
    <>
      {isPost && (
        <div className="mb-6">
          <Form form={form} onFinish={handleCreatePost} layout="vertical">
            <Card
              loading={!dataUser}
              bodyStyle={{ paddingBottom: 0 }}
              actions={
                dataUser && [
                  <div className="flex flex-col lg:flex-row lg:justify-between w-full border-t border-black mt-4 pt-4 px-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        style={
                          isPostBroadcastedTo?.audienceType !== "PRIVATE"
                            ? { backgroundColor: "#158284" }
                            : {}
                        }
                        onChange={(e) =>
                          e
                            ? isPostBroadcastedToSet({
                                audienceType: "PUBLIC",
                                audienceName: "Everyone",
                              })
                            : isPostBroadcastedToSet({
                                audienceType: "PRIVATE",
                              })
                        }
                      />

                      <div className="flex gap-1">
                        <span>Broadcast</span>
                        {isPostBroadcastedTo?.audienceType !== "PRIVATE" && (
                          <span className="flex gap-1">
                            {" "}
                            to{" "}
                            <p
                              role="button"
                              className="text-yellow"
                              onClick={() => isOpenBroadcastModalSet(true)}
                            >
                              {truncateText(
                                isPostBroadcastedTo?.audienceName as string,
                                { lg: 40, sm: 40 },
                              )}{" "}
                              <BsChevronDown />
                            </p>
                          </span>
                        )}
                        <Tooltip
                          placement={isMobile ? "topRight" : "right"}
                          overlayInnerStyle={{ background: "transparent" }}
                          overlayClassName={
                            isMobile
                              ? "origin-[unset!important] left-[0!important] w-[100%!important] max-w-[100%!important]"
                              : undefined
                          }
                          title={
                            <div className="text-sm bg-[#3C3C3C] p-2 rounded-sm w-full lg:w-[496px]">
                              <p>
                                The broadcast feature lets you decide who gets
                                to see your post.{" "}
                              </p>
                              <ul>
                                <li>
                                  - Broadcast Off: Your post stays on your page
                                  only.{" "}
                                </li>
                                <li>
                                  - Broadcast On: Want to share it with a bigger
                                  audience? Turn it on and choose:{" "}
                                </li>
                                <ul className="pl-4">
                                  <li>
                                    - Everyone: Share with all connections
                                    across your organization.
                                  </li>
                                  <li>
                                    - Your Department: Share with everyone in
                                    your department.{" "}
                                  </li>
                                  <li>
                                    - Your Circles: Share your post with
                                    selected circles.
                                  </li>
                                </ul>
                              </ul>
                            </div>
                          }
                        >
                          <QuestionCircleFilled className="ml-1 text-white hover:text-white" />
                        </Tooltip>
                      </div>
                    </div>
                    <div className="flex mt-4 lg:mt-0">
                      <div className="flex-1 lg:flex-none">
                        <Upload
                          accept="*"
                          maxCount={1}
                          showUploadList={false}
                          multiple={false}
                          fileList={[]}
                          onChange={(e: any) => setFileRaw(e.file)}
                        >
                          <Button
                            icon={<FaRegImage />}
                            type="text"
                            htmlType="button"
                            disabled={isProcess}
                          >
                            Attach File
                          </Button>
                        </Upload>
                      </div>
                      <div className="flex-1 flex:none">
                        <Button
                          icon={<SendOutlined />}
                          type="text"
                          htmlType="submit"
                          loading={isProcess}
                        >
                          Post
                        </Button>
                      </div>
                    </div>
                  </div>,
                ]
              }
            >
              <div className="flex gap-3 items-center pb-4">
                <div className="inline-block">
                  <CardUserProfile
                    item={dataUser?.user}
                    points={dataUser?.user?.points}
                    cgPercent={dataUser?.CGPercent}
                    avatarOnly
                    avatarSize="xs"
                    findCG
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-bold text-white">
                    {`${dataUser?.user?.firstName} ${dataUser?.user?.lastName}`}
                  </h4>
                </div>
              </div>
              <Form.Item
                name={"content"}
                rules={[{ required: true }]}
                style={{ margin: 0 }}
              >
                <Input.TextArea
                  maxLength={50000}
                  placeholder="What's on your mind?"
                  style={{ resize: "none", marginBottom: ".25rem" }}
                  allowClear
                  rows={5}
                  disabled={isProcess}
                />
              </Form.Item>

              {!isEmpty(fileRaw) && (
                <div className="flex w-full gap-4 justify-between mt-4 p-2 bg-neutral-700">
                  <div>
                    <HiPhotograph className="inline-block mr-1" />{" "}
                    <span className="break-all">{fileRaw?.name}</span>
                  </div>
                  <Button
                    type="text"
                    icon={<HiTrash className="text-red-400" />}
                    size="small"
                    onClick={() => {
                      setFileRaw(undefined);
                    }}
                  />
                </div>
              )}
            </Card>
          </Form>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-6">
        {isLoading ? (
          <>
            <div className="animate-pulse">
              <div className="w-full h-24 bg-neutral-700 rounded-lg" />
            </div>
            <div className="animate-pulse">
              <div className="w-full h-24 bg-neutral-700 rounded-lg" />
            </div>
            <div className="animate-pulse">
              <div className="w-full h-24 bg-neutral-700 rounded-lg" />
            </div>
          </>
        ) : !isEmpty(data) && !isError ? (
          data?.map((item, idx: number) => (
            <ProfileCardPosted
              dataPost={item}
              key={`card-posted-${idx}`}
              dataUser={dataUser}
              onSuccess={() => refetch()}
              isVisiting={!isPost}
            />
          ))
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </div>

      <ProfileModalBroadcast
        open={isOpenBroadcastModal}
        myDepartment={myDepartment}
        onChange={(val) => isPostBroadcastedToSet(val)}
        selected={isPostBroadcastedTo}
        onCancel={() => {
          isOpenBroadcastModalSet(false);
        }}
      />
    </>
  );
};

export default ProfileSectionPosts;
